import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  LinearProgress,
  Link,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { CampaignsContext } from "contexts/Campaigns";
import { OrganizationUserContext } from "contexts/Organization";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { Route, Routes, useNavigate, useParams } from "react-router-dom";
import {
  Campaign,
  CampaignEmailStatus,
  CampaignOutreachContactRow,
  CampaignStats,
} from "schemas/dashboard";
import { routes } from "schemas/routes";

import { fetcherAuth } from "utils/api";
import { formatDate, formatEpochTimestamp } from "utils/time";

import OutreachContactDrawer from "../../ContactList/OutreachContactDrawer";
import BrandLogo from "../../Discover/Shared/BrandLogo";
import { CampaignStatusChip } from "../CampaignStatusChip";
import CampaignEmailStatusChip from "./CampaignEmailStatusChip";
import CampaignScheduledDrawer from "./CampaignScheduledDrawer";

const PER_PAGE = 100;

type PitchTab = "scheduled" | "sent" | "failed";

const headerCellSx = {
  fontWeight: 600,
  fontSize: 11,
  textTransform: "uppercase" as const,
  color: "#4A5565",
  letterSpacing: 0.5,
  backgroundColor: "#f1f0ec",
};

function shortRelative(epoch?: number | null): string {
  if (!epoch) return "";
  const mins = moment.unix(epoch).diff(moment(), "minutes");
  const future = mins >= 0;
  const abs = Math.abs(mins);
  let value: string;
  if (abs < 60) value = `${Math.max(1, abs)}m`;
  else if (abs < 60 * 48) value = `${Math.round(abs / 60)}h`;
  else value = `${Math.round(abs / (60 * 24))}d`;
  return future ? `in ${value}` : `${value} ago`;
}

function formatNextSend(epoch: number, tz?: string | null): string {
  const m = tz ? moment.unix(epoch).tz(tz) : moment.unix(epoch);
  const now = tz ? moment().tz(tz) : moment();
  const time = m.minute() === 0 ? m.format("ha") : m.format("h:mma");
  if (m.isSame(now, "day")) return `Today at ${time}`;
  if (m.isSame(now.clone().add(1, "day"), "day")) return `Tomorrow at ${time}`;
  if (m.isSame(now, "week")) return `${m.format("dddd")} at ${time}`;
  return m.format("MMM D, h:mm A");
}

function StatCard({
  label,
  value,
  sub,
  valueColor,
}: {
  label: string;
  value: React.ReactNode;
  sub: string;
  valueColor?: string;
}) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 140,
        border: `1px solid ${grey[200]}`,
        borderRadius: 2,
        p: 1.5,
        bgcolor: "#fff",
      }}
    >
      <Typography
        sx={{
          fontSize: 11,
          color: grey[600],
          textTransform: "uppercase",
          letterSpacing: 0.5,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 22,
          fontWeight: 700,
          lineHeight: 1.3,
          color: valueColor,
        }}
      >
        {value}
      </Typography>
      <Typography sx={{ fontSize: 11, color: grey[500] }}>{sub}</Typography>
    </Box>
  );
}

const SELECTED_GREEN = "#165C4E";
const SELECTED_GREEN_BG = "#DBEFDD";

const TABS: { value: PitchTab; label: string; icon: string }[] = [
  { value: "scheduled", label: "Scheduled", icon: "fa-regular fa-clock" },
  { value: "sent", label: "Sent", icon: "fa-regular fa-paper-plane" },
  {
    value: "failed",
    label: "Failed",
    icon: "fa-regular fa-circle-exclamation",
  },
];

export default function CampaignDetail() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { getCampaign } = useContext(CampaignsContext);
  const { setAlert, setErrorAlert } = useContext(AlertContext);

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [stats, setStats] = useState<CampaignStats | null>(null);
  const [rows, setRows] = useState<CampaignOutreachContactRow[]>([]);
  const [counts, setCounts] = useState<{
    sent: number;
    scheduled: number;
    failed: number;
    replied: number;
    nextSendAt: number | null;
  }>({ sent: 0, scheduled: 0, failed: 0, replied: 0, nextSendAt: null });
  const [page, setPage] = useState<number>(1);
  const [tableLoading, setTableLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [retrying, setRetrying] = useState<boolean>(false);
  const [archivingNoContact, setArchivingNoContact] = useState<boolean>(false);
  const [pitchTab, setPitchTab] = useState<PitchTab>("scheduled");

  useEffect(() => {
    const loadMeta = async () => {
      if (!currentOrg?.id || !campaignId) return;
      const result = await getCampaign(Number(campaignId), 1, 1);
      if (result?.campaign) setCampaign(result.campaign);
      try {
        const { campaignStats } = await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg.id}/campaigns/stats?campaign_ids=${campaignId}`,
          "GET",
        );
        if (campaignStats?.length) setStats(campaignStats[0]);
      } catch {
        // stat strip is non-critical; ignore failures
      }
    };
    loadMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, currentOrg?.id]);

  useEffect(() => {
    fetchRows(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, currentOrg?.id, pitchTab]);

  useEffect(() => {
    if (pitchTab === "failed" && counts.failed === 0) setPitchTab("scheduled");
  }, [counts.failed, pitchTab]);

  const fetchRows = async (nextPage: number, reset: boolean) => {
    if (!currentOrg?.id || !campaignId) return;
    if (reset) setTableLoading(true);
    else setLoadingMore(true);
    try {
      const { outreachContacts, counts: nextCounts } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/campaigns/${campaignId}/outreach-contacts?status_group=${pitchTab}&page=${nextPage}&per_page=${PER_PAGE}`,
        "GET",
      );
      if (nextCounts) setCounts(nextCounts);
      setRows((prev) =>
        reset ? outreachContacts : [...prev, ...outreachContacts],
      );
      setPage(nextPage);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      if (reset) setTableLoading(false);
      else setLoadingMore(false);
    }
  };

  const postAction = async (path: string, successMsg: string) => {
    if (!currentOrg?.id || !campaignId) return;
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/campaigns/${campaignId}${path}`,
        "POST",
      );
      setAlert(successMsg, "success");
      fetchRows(1, true);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const actOnRow = async (
    row: CampaignOutreachContactRow,
    path: string,
    successMsg: string,
    becomesScheduled: boolean,
  ) => {
    if (!currentOrg?.id || !campaignId) return;
    setRows((prev) =>
      prev.filter((r) => r.campaignBrandId !== row.campaignBrandId),
    );
    setCounts((c) => ({
      ...c,
      failed: Math.max(0, c.failed - 1),
      scheduled: becomesScheduled ? c.scheduled + 1 : c.scheduled,
    }));
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/campaigns/${campaignId}${path}`,
        "POST",
      );
      setAlert(successMsg, "success");
    } catch (error) {
      setErrorAlert(error);
      fetchRows(1, true); // restore the table if the action failed
    }
  };

  const handleRetryAll = async () => {
    setRetrying(true);
    try {
      await postAction("/retry-failed", "Retrying all failed brands");
    } finally {
      setRetrying(false);
    }
  };

  const handleArchiveNoContact = async () => {
    setArchivingNoContact(true);
    try {
      await postAction("/archive-no-contact", "Archived no-contact brands");
    } finally {
      setArchivingNoContact(false);
    }
  };

  const totalCount = counts.sent + counts.scheduled + counts.failed;
  const progressPct = totalCount
    ? Math.round((counts.sent / totalCount) * 100)
    : 0;
  const openRate = Math.round((stats?.openedPercentage ?? 0) * 100);
  const replyRate = counts.sent
    ? Math.round((counts.replied / counts.sent) * 100)
    : 0;
  const tabTotal = counts[pitchTab];
  const showDate = pitchTab !== "failed";
  const showOpens = pitchTab === "sent";
  const colCount = 3 + (showDate ? 1 : 0) + (showOpens ? 1 : 0) + 1;

  const actionLink = (label: string, onClick: () => void, color?: string) => (
    <Link
      component="button"
      underline="always"
      sx={{ fontSize: 12, color }}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      {label}
    </Link>
  );

  const renderAction = (row: CampaignOutreachContactRow) => {
    if (pitchTab === "sent") {
      return actionLink("Thread", () =>
        navigate(`contact/${row.outreachContactId}`),
      );
    }
    switch (row.campaignEmailStatus) {
      case CampaignEmailStatus.scheduled:
        return actionLink("Edit", () =>
          navigate(`scheduled/${row.outreachContactId}`),
        );
      case CampaignEmailStatus.failed:
      case CampaignEmailStatus.cancelled:
        return actionLink("Retry", () =>
          actOnRow(
            row,
            `/brands/${row.campaignBrandId}/reschedule`,
            "Retrying outreach",
            true,
          ),
        );
      case CampaignEmailStatus.no_contact:
        return actionLink(
          "Archive",
          () =>
            actOnRow(
              row,
              `/brands/${row.campaignBrandId}/archive`,
              "Brand archived",
              false,
            ),
          grey[600],
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        px: { xs: 0.5, md: 1 },
        pt: 1,
        pb: 4,
        height: "100%",
        overflowY: "auto",
      }}
    >
      {!campaign ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Skeleton variant="rounded" width={320} height={36} />
          <Skeleton variant="rounded" width="100%" height={48} />
          <Skeleton variant="rounded" width="100%" height={240} />
        </Box>
      ) : (
        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: 3,
            p: { xs: 2, md: 3 },
          }}
        >
          <Button
            color="secondary"
            size="small"
            onClick={() => navigate(`/${routes.campaignView}`)}
            sx={{ mb: 1.5, textTransform: "none", ml: -0.5 }}
          >
            <Box
              component="i"
              className="fa-solid fa-chevron-left"
              sx={{ mr: 1 }}
            />
            Campaigns
          </Button>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {campaign.name}
            </Typography>
            {campaign.status && (
              <CampaignStatusChip
                status={campaign.status}
                startDate={campaign.startDate ?? undefined}
              />
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              color: grey[600],
              mb: 2,
            }}
          >
            <Typography variant="body2" sx={{ color: grey[600] }}>
              <Box
                component="i"
                className="fa-regular fa-calendar"
                sx={{ mr: 0.5 }}
              />
              {formatDate(campaign.startDate)} – {formatDate(campaign.endDate)}
            </Typography>
            <Typography variant="body2" sx={{ color: grey[600] }}>
              <Box
                component="i"
                className="fa-solid fa-users"
                sx={{ mr: 0.5 }}
              />
              {totalCount} brands
            </Typography>
            {campaign.timezone && (
              <Typography variant="body2" sx={{ color: grey[600] }}>
                <Box
                  component="i"
                  className="fa-regular fa-clock"
                  sx={{ mr: 0.5 }}
                />
                {campaign.timezone}
              </Typography>
            )}
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ color: grey[600], mb: 0.5 }}>
              <strong>{counts.sent}</strong> of {totalCount} sent ·{" "}
              {progressPct}%
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progressPct}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: "#f1f0ec",
                "& .MuiLinearProgress-bar": { backgroundColor: "#014638" },
              }}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
            <StatCard
              label="Sent"
              value={
                <>
                  {counts.sent}
                  <Box
                    component="span"
                    sx={{ fontSize: 14, color: grey[500], fontWeight: 400 }}
                  >
                    {" "}
                    / {totalCount}
                  </Box>
                </>
              }
              sub={`${progressPct}% complete`}
              valueColor="#166534"
            />
            <StatCard
              label="Scheduled"
              value={counts.scheduled}
              sub={
                counts.nextSendAt
                  ? `Next: ${formatNextSend(counts.nextSendAt, campaign.timezone)}`
                  : "to send"
              }
            />
            <StatCard
              label="Opens"
              value={`${openRate}%`}
              sub={`${stats?.openedCount ?? 0} total opens`}
            />
            <StatCard
              label="Replies"
              value={`${replyRate}%`}
              sub={`${counts.replied} brands replied`}
              valueColor={replyRate > 1 ? "#16a34a" : undefined}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
              mb: 1.5,
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                gap: 0.5,
                p: 0.5,
                borderRadius: 2,
                backgroundColor: grey[200],
              }}
            >
              {TABS.filter(
                (t) => t.value !== "failed" || counts.failed > 0,
              ).map((t) => {
                const selected = pitchTab === t.value;
                return (
                  <Box
                    key={t.value}
                    onClick={() => setPitchTab(t.value)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      cursor: "pointer",
                      px: 1.75,
                      py: 0.625,
                      borderRadius: 1.5,
                      fontSize: 12,
                      fontWeight: 500,
                      color: selected ? "text.primary" : grey[700],
                      backgroundColor: selected ? "#fff" : "transparent",
                      boxShadow: selected
                        ? "0 1px 2px rgba(0,0,0,0.12)"
                        : "none",
                    }}
                  >
                    <Box
                      component="i"
                      className={t.icon}
                      sx={{ fontSize: 11 }}
                    />
                    {t.label}
                    <Box
                      component="span"
                      sx={{
                        backgroundColor: selected
                          ? SELECTED_GREEN_BG
                          : grey[300],
                        color: selected ? SELECTED_GREEN : grey[700],
                        borderRadius: 10,
                        px: 0.75,
                        minWidth: 18,
                        textAlign: "center",
                        fontSize: 10,
                        fontWeight: 600,
                      }}
                    >
                      {counts[t.value]}
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {pitchTab === "failed" && (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title="We'll try to find a new contact for each failed brand. Brands with no contact at all won't be retried.">
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={retrying}
                      onClick={handleRetryAll}
                      sx={{ textTransform: "none" }}
                    >
                      <Box
                        component="i"
                        className="fa-regular fa-arrow-rotate-right"
                        sx={{ mr: 0.75, fontSize: 12 }}
                      />
                      {retrying ? "Retrying…" : "Retry Failed"}
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Archive every brand we couldn't find a contact for. They'll drop out of the campaign and won't count toward progress.">
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={archivingNoContact}
                      onClick={handleArchiveNoContact}
                      sx={{ textTransform: "none" }}
                    >
                      <Box
                        component="i"
                        className="fa-regular fa-box-archive"
                        sx={{ mr: 0.75, fontSize: 12 }}
                      />
                      {archivingNoContact ? "Archiving…" : "Archive No Contact"}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            )}
          </Box>

          <TableContainer
            sx={{ border: `1px solid ${grey[200]}`, borderRadius: 2 }}
          >
            <Table
              size="small"
              sx={{
                tableLayout: "fixed",
                "& .MuiTableCell-root": { px: 1.5, py: 1.25 },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellSx, width: "30%" }}>
                    Brand
                  </TableCell>
                  <TableCell sx={{ ...headerCellSx, width: "26%" }}>
                    Contact
                  </TableCell>
                  <TableCell sx={headerCellSx}>Status</TableCell>
                  {showDate && (
                    <TableCell sx={headerCellSx}>
                      {pitchTab === "scheduled" ? "Sending" : "Sent"}
                    </TableCell>
                  )}
                  {showOpens && (
                    <TableCell
                      sx={{ ...headerCellSx, width: 80 }}
                      align="center"
                    >
                      Opens
                    </TableCell>
                  )}
                  <TableCell
                    sx={{ ...headerCellSx, width: 100 }}
                    align="center"
                  />
                </TableRow>
              </TableHead>
              <TableBody>
                {tableLoading ? (
                  <TableRow>
                    <TableCell colSpan={colCount} sx={{ py: 1 }}>
                      <Skeleton variant="rounded" height={120} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={colCount}
                      align="center"
                      sx={{ py: 5, color: grey[500] }}
                    >
                      {pitchTab === "scheduled"
                        ? "Nothing queued to send."
                        : pitchTab === "sent"
                          ? "No pitches sent yet."
                          : "No failed brands."}
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => {
                    const brand =
                      row.bentoBrand ?? row.outreachContact?.bentoBrand;
                    const isScheduledRow =
                      row.campaignEmailStatus === CampaignEmailStatus.scheduled;
                    const when = isScheduledRow ? row.scheduledAt : row.sentAt;
                    // Pending/paused/failed rows aren't openable from the row
                    // itself (failed acts via the Retry link instead).
                    const clickable =
                      row.outreachContactId != null &&
                      row.campaignEmailStatus !== CampaignEmailStatus.failed &&
                      (pitchTab !== "scheduled" || isScheduledRow);
                    const openRow = () =>
                      navigate(
                        isScheduledRow
                          ? `scheduled/${row.outreachContactId}`
                          : `contact/${row.outreachContactId}`,
                      );
                    return (
                      <TableRow
                        key={row.campaignBrandId}
                        hover={clickable}
                        onClick={clickable ? openRow : undefined}
                        sx={{ cursor: clickable ? "pointer" : "default" }}
                      >
                        <TableCell sx={{ overflow: "hidden" }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              minWidth: 0,
                            }}
                          >
                            {brand && <BrandLogo bentoBrand={brand} />}
                            <Typography
                              noWrap
                              fontWeight={700}
                              sx={{ minWidth: 0, fontSize: 13 }}
                            >
                              {brand?.brandName ?? "—"}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ overflow: "hidden" }}>
                          <Typography
                            noWrap
                            sx={{ color: grey[600], fontSize: 11 }}
                          >
                            <Box
                              component="i"
                              className="fa-regular fa-user"
                              sx={{ mr: 0.5, fontSize: 10, color: grey[500] }}
                            />
                            {row.outreachContact?.contactTitle ||
                              row.outreachContact?.contactName ||
                              "n/a"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <CampaignEmailStatusChip
                            status={row.campaignEmailStatus}
                            followUpNumber={row.followUpNumber}
                          />
                        </TableCell>
                        {showDate && (
                          <TableCell>
                            {when ? (
                              <>
                                <Typography
                                  variant="body2"
                                  fontWeight={500}
                                  noWrap
                                >
                                  {formatEpochTimestamp({
                                    epochTimestamp: when,
                                    fullDate: true,
                                  })}
                                </Typography>
                                <Typography
                                  sx={{ fontSize: 10, color: grey[500] }}
                                >
                                  {shortRelative(when)}
                                </Typography>
                              </>
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{ color: grey[400] }}
                              >
                                —
                              </Typography>
                            )}
                          </TableCell>
                        )}
                        {pitchTab === "sent" && (
                          <TableCell align="center">
                            {row.opens > 0 ? (
                              <Typography variant="body2">
                                <Box
                                  component="i"
                                  className="fa-regular fa-eye"
                                  sx={{ mr: 0.5, fontSize: 12 }}
                                />
                                {row.opens}
                              </Typography>
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{ color: grey[400] }}
                              >
                                —
                              </Typography>
                            )}
                          </TableCell>
                        )}
                        <TableCell
                          align="center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {renderAction(row)}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {!tableLoading && rows.length < tabTotal && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Button
                type="button"
                variant="outlined"
                disabled={loadingMore}
                onClick={(e) => {
                  e.preventDefault();
                  fetchRows(page + 1, false);
                }}
                sx={{ textTransform: "none" }}
              >
                {loadingMore ? "Loading…" : "View more"}
              </Button>
            </Box>
          )}
        </Box>
      )}

      <Routes>
        <Route
          path="contact/:outreachContactId/*"
          element={<OutreachContactDrawer />}
        />
        <Route
          path="scheduled/:outreachContactId/*"
          element={<CampaignScheduledDrawer />}
        />
      </Routes>
    </Box>
  );
}
